import { Component, Injectable, OnInit, ViewEncapsulation } from '@angular/core';
import { NgbTypeaheadSelectItemEvent } from '@ng-bootstrap/ng-bootstrap';
import { map, OperatorFunction } from 'rxjs';
import { SearchService } from 'src/app/components/search/search.service';
import { OptionsChangesContent, SelectorOption } from 'src/app/interfaces/form-and-field.interface';
import { SearchParams } from 'src/app/interfaces/search.interface';
import { DropdownFieldsProcessorKind, stringToSelectOptionAdapter } from '../selector-options.helpers';
import { SelectorOptionsService } from '../selector-options.service';
import { SuggestionFieldShared } from '../suggestion-field.shared';

@Component({
  selector: 'app-single-dropdown-form-field',
  templateUrl: './single-dropdown-form-field.component.html',
  styleUrls: ['./single-dropdown-form-field.component.scss'],
  encapsulation: ViewEncapsulation.None,
  providers: [SelectorOptionsService, SearchService],
})
@Injectable()
export class SingleDropdownFormFieldComponent extends SuggestionFieldShared implements OnInit {
  public selectedItem: SelectorOption | string = '';
  public controlValueLength = 0;

  private processorKind: DropdownFieldsProcessorKind;
  private suggestions: SelectorOption[] = [];

  public override ngOnInit() {
    super.ngOnInit();
    if (this.selectedItem) {
      this.controlValueLength = (this.selectedItem as SelectorOption).title.length;
    }
  }

  public checkErrors(): void {
    // No-op implementation
  }

  public validateInputValue(value: string): void {
    this.formValidationService.checkSingleSearchFieldErrors(this.control, value);
    if (this.selectedItem && (this.selectedItem as SelectorOption).title) {
      this.control.setErrors(
        this.formValidationService.getSearchMultipleFieldValidationErrors(
          (this.selectedItem as SelectorOption).title,
          this.fieldAttribute.backendAttribute,
        ),
      );
    }
  }

  protected getOptionsOperatorFunction(): OperatorFunction<SearchParams, SelectorOption[]> {
    return map((params: SearchParams) => this.filterSelectorOptions(params));
  }

  /**
   * @description Sets up isEditable value. Sets up the selectedItem depending
   *              on control value. Calls subscribeTo...() methods.
   */
  protected initSelectedItems(): void {
    this.processorKind = this.optionsService.getProcessorType(this.fieldAttribute);

    if (this.control.value) {
      this.selectedItem = this.control.value as SelectorOption;
    }

    this.subscribeToControlChanges();
    this.subscribeToOptionsChanges();
  }

  public onSelectedItem(event?: NgbTypeaheadSelectItemEvent): void {
    const value: SelectorOption = event.item as SelectorOption;
    this.setControlValueLength(value);
    this.updateControlValue(value);
  }

  public onInput(value: string): void {
    this.setControlValueLength(value);
    this.updateControlValue(value);
  }

  public onBlur(): void {
    if (typeof this.selectedItem === 'string') {
      this.selectedItem = this.selectedItem.trim();
    }

    if (typeof this.selectedItem === 'object' && 'title' in this.selectedItem) {
      this.selectedItem.title = this.selectedItem.title.trim();
    }

    this.setControlValueLength(this.selectedItem);
    this.updateControlValue(this.selectedItem);
  }

  public setControlValueLength(value: string | SelectorOption): void {
    if (value) {
      if (typeof value === 'string') {
        this.controlValueLength = value.length;
      }

      if (typeof value === 'object' && 'title' in value) {
        this.controlValueLength = value.title.length;
      }
    } else {
      this.controlValueLength = 0;
    }
  }

  public updateControlValue(value: string | SelectorOption): void {
    let testedValue = '';
    if (this.processorKind !== DropdownFieldsProcessorKind.default) {
      this.updateEditableControlValue(value);
    } else {
      testedValue = this.updateNotEditableControlValue(value);
    }
    this.validateInputValue(testedValue);
  }

  protected onControlDisabled(): void {
    this.selectedItem = '';
  }

  /**
   * @description Filters the search suggestions list depending on input value (term).
   *              Returns first 10 items from it.
   * @param { SearchParams } params Inputted value.
   * @returns { SelectorOption[] }
   */
  private filterSelectorOptions(params: SearchParams): SelectorOption[] {
    return params.searchTerm === ''
      ? this.suggestions
      : this.suggestions.filter((v: SelectorOption) => {
          return v.title.toLowerCase().indexOf(params.searchTerm.toLowerCase()) > -1;
        });
  }

  /**
   * @description Updates control value depend on the selected item or entered
   *              value. Updates selectedItem depending on its type. Note that we
   *              have event only on blur here
   */
  public updateEditableControlValue(value: string | SelectorOption): void {
    this.selectedItem = value || '';
    if (value === '') {
      this.selectedItem = '';
    } else {
      this.selectedItem = typeof value === 'string' ? stringToSelectOptionAdapter(value) : this.selectedItem;
    }

    this.control.setValue(this.selectedItem);
  }

  /**
   * @description Updates selectedItem and control values depend on the selected item
   *              from the search suggestions list.
   *              Returns dumpy value to use it for validation.
   * @returns { string }
   */
  public updateNotEditableControlValue(value: string | SelectorOption): string {
    const foundItem: SelectorOption = this.suggestions.find((item: SelectorOption) => {
      return item === value || item.title === value;
    });

    this.selectedItem = foundItem || value;
    this.control.setValue(foundItem || undefined);

    return this.control.value ? '' : this.selectedItem.toString();
  }

  /**
   * @description Subscribes on optionsSubject changes and updates
   *              the selectors in case passed conditions.
   *              Inits suggestions options.
   */
  private subscribeToOptionsChanges(): void {
    this.subscriptions.push(
      this.optionsService.optionsSubject.subscribe((content: OptionsChangesContent) => {
        if (this.optionsService.isCurrentField(this.fieldAttribute, content)) {
          this.suggestions = content.options;
        }
      }),
    );

    this.optionsService.initOptions(this.fieldAttribute);
  }
}
